import { Injectable } from '@angular/core';
import { CanvasBranch } from './canvas-workflow.interface';

@Injectable({
  providedIn: 'root'
})
export class PanZoomService {

  // État du zoom et pan
  zoomLevel = 1;
  minZoom = 0.1;
  maxZoom = 5;
  zoomStep = 0.1;
  panOffset = { x: 0, y: 0 };
  isPanning = false;
  lastPanPosition = { x: 0, y: 0 };

  /**
   * Augmente le niveau de zoom
   */
  zoomIn(): void {
    if (this.zoomLevel < this.maxZoom) {
      this.zoomLevel = Math.min(this.maxZoom, this.zoomLevel + this.zoomStep);
    }
  }

  /**
   * Diminue le niveau de zoom
   */
  zoomOut(): void {
    if (this.zoomLevel > this.minZoom) {
      this.zoomLevel = Math.max(this.minZoom, this.zoomLevel - this.zoomStep);
    }
  }

  /**
   * Remet le zoom et le pan aux valeurs par défaut
   */
  resetZoom(): void {
    this.zoomLevel = 1;
    this.panOffset = { x: 0, y: 0 };
  }

  /**
   * Ajuste le zoom et le pan pour afficher tout le workflow centré
   */
  fitToScreen(allBranches: CanvasBranch[], viewportWidth: number = 800, viewportHeight: number = 600): void {
    if (allBranches.length === 0) return;

    const minX = Math.min(...allBranches.map(c => c.position.x));
    const maxX = Math.max(...allBranches.map(c => c.position.x + c.width));
    const minY = Math.min(...allBranches.map(c => c.position.y));
    const maxY = Math.max(...allBranches.map(c => c.position.y + c.height));

    const workflowWidth = maxX - minX;
    const workflowHeight = maxY - minY;

    const scaleX = viewportWidth / workflowWidth;
    const scaleY = viewportHeight / workflowHeight;
    this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, Math.min(scaleX, scaleY) * 0.9));

    // Centrer le workflow dans le viewport
    const workflowCenterX = (minX + maxX) / 2;
    const workflowCenterY = (minY + maxY) / 2;

    this.panOffset.x = (viewportWidth / 2) - (workflowCenterX * this.zoomLevel);
    this.panOffset.y = (viewportHeight / 2) - (workflowCenterY * this.zoomLevel);
  }

  /**
   * Centre le workflow dans le viewport au niveau de zoom actuel
   */
  centerWorkflow(allBranches: CanvasBranch[], viewportWidth: number = 800, viewportHeight: number = 600): void {
    if (allBranches.length === 0) return;

    const minX = Math.min(...allBranches.map(c => c.position.x));
    const maxX = Math.max(...allBranches.map(c => c.position.x + c.width));
    const minY = Math.min(...allBranches.map(c => c.position.y));
    const maxY = Math.max(...allBranches.map(c => c.position.y + c.height));

    const workflowCenterX = (minX + maxX) / 2;
    const workflowCenterY = (minY + maxY) / 2;

    this.panOffset.x = (viewportWidth / 2) - (workflowCenterX * this.zoomLevel);
    this.panOffset.y = (viewportHeight / 2) - (workflowCenterY * this.zoomLevel);
  }

  /**
   * Gère le zoom via la molette de la souris
   */
  handleMouseWheel(event: WheelEvent, targetElement: HTMLElement): void {
    event.preventDefault();

    const delta = event.deltaY > 0 ? -this.zoomStep : this.zoomStep;
    const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel + delta));

    if (newZoom !== this.zoomLevel) {
      const rect = targetElement.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      const zoomRatio = newZoom / this.zoomLevel;
      this.panOffset.x = mouseX - (mouseX - this.panOffset.x) * zoomRatio;
      this.panOffset.y = mouseY - (mouseY - this.panOffset.y) * zoomRatio;
      this.zoomLevel = newZoom;
    }
  }

  /**
   * Démarre le panning
   */
  startPanning(event: MouseEvent, isDragElement: boolean = false): void {
    if (isDragElement) return;

    if (event.button === 0 || event.button === 1) {
      event.preventDefault();
      this.isPanning = true;
      this.lastPanPosition = { x: event.clientX, y: event.clientY };
    }
  }

  /**
   * Met à jour la position du pan pendant le déplacement
   */
  updatePanning(
    event: MouseEvent,
    canvasSize: { width: number, height: number },
    viewportSize: { width: number, height: number }
  ): void {
    if (!this.isPanning) return;

    const deltaX = event.clientX - this.lastPanPosition.x;
    const deltaY = event.clientY - this.lastPanPosition.y;

    // Permettre un panning infini - pas de limites
    this.panOffset.x += deltaX;
    this.panOffset.y += deltaY;

    this.lastPanPosition = { x: event.clientX, y: event.clientY };
  }

  /**
   * Réajuste le panOffset pour rester dans les limites du canvas
   * Note: avec le panning infini, cette méthode ne fait plus rien mais est conservée pour la compatibilité
   */
  clampPanOffset(canvasSize: { width: number, height: number }, viewportSize: { width: number, height: number }): void {
    // Panning infini activé - pas de clamping
  }


  /**
   * Arrête le panning
   */
  stopPanning(): void {
    this.isPanning = false;
  }

  /**
   * Retourne la transformation CSS pour le canvas
   */
  getCanvasTransform(): string {
    return `scale(${this.zoomLevel}) translate(${this.panOffset.x / this.zoomLevel}px, ${this.panOffset.y / this.zoomLevel}px)`;
  }

  /**
   * Calcule le style du container du canvas pour un panning infini
   */
  getCanvasContainerStyle(
    canvasSize: { width: number, height: number },
    windowWidth: number = 800,
    windowHeight: number = 600
  ): any {
    // Pour un panning infini, le container doit toujours remplir le viewport
    // mais sans contraindre le déplacement
    return {
      'width': '100%',
      'height': '100%',
      'min-width': '100%',
      'min-height': '100%',
      'position': 'relative',
      'overflow': 'visible'
    };
  }

  /**
   * Retourne le pourcentage de zoom
   */
  get zoomPercentage(): number {
    return Math.round(this.zoomLevel * 100);
  }
}